import React, { useRef, useEffect } from 'react';
import { useCrowd } from './context/CrowdContext';
import { QSTAGES, XSTAGES, QLINES } from './config/crowdConfig';
import { fmt, losColor, crowdWord, cellDens, xDens, qKey } from './utils/crowdMath';
import {
  Video,
  X,
  Users,
  Activity,
  Gauge,
  LogOut,
  ArrowUpRight
} from 'lucide-react';

export const CameraModal: React.FC = () => {
  const { activeCameraModal, closeCamera, queueState, holdingComplexes } = useCrowd();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeCamera();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [closeCamera]);

  const queueStateRef = useRef(queueState);
  queueStateRef.current = queueState;

  const holdingComplexesRef = useRef(holdingComplexes);
  holdingComplexesRef.current = holdingComplexes;

  // Determine if this is an exit camera (strictly based on kind === 'exit')
  const isExit = Boolean(
    activeCameraModal && activeCameraModal.kind === 'exit'
  );

  useEffect(() => {
    if (!activeCameraModal) return;

    if (activeCameraModal.stream && videoRef.current) {
      videoRef.current.src = activeCameraModal.stream;
      videoRef.current.play().catch(() => { });
      return;
    }

    const cv = canvasRef.current;
    if (!cv) return;

    const currentQState = queueStateRef.current;
    const currentHolding = holdingComplexesRef.current;

    // Check if camera belongs to a holding room
    let holdingRoom: { currentCount: number; capacity: number } | null = null;
    for (const ha of currentHolding) {
      for (const rm of ha.rooms) {
        if (rm.cams.includes(activeCameraModal.id)) {
          holdingRoom = rm;
          break;
        }
      }
      if (holdingRoom) break;
    }

    const stage = QSTAGES.find(s => s.id === activeCameraModal.stage) || XSTAGES.find(s => s.id === activeCameraModal.stage);
    const line = QLINES.find(l => l.id === activeCameraModal.line);
    const initialDens = holdingRoom
      ? Math.max(0.6, Math.min(5.5, (holdingRoom.currentCount / holdingRoom.capacity) * 3.8))
      : line && stage ? cellDens(line.id, stage, currentQState.cells)
      : stage ? xDens(stage as any, currentQState.xcells) : 1.4;
    const initialPeople = holdingRoom
      ? holdingRoom.currentCount
      : line && stage ? (currentQState.cells[qKey(line.id, stage.id)] || 0)
      : stage ? (currentQState.xcells[stage.id] || 0) : currentQState.hold;

    const devoteeColors = [
      '#F59E0B', '#EA580C', '#E2E8F0', '#38BDF8',
      '#B91C1C', '#FBBF24', '#10B981', '#A855F7',
      '#D97706', '#0284C7', '#F43F5E', '#F1F5F9'
    ];

    // 3 Distinct Queue Lane Centers across the corridor (for queue mode)
    const laneCenters = [0.24, 0.50, 0.76];

    interface PersonParticle {
      lane: number;
      laneOffset: number;
      x: number;
      y: number;
      vx: number;
      vy: number;
      s: number;
      state: 'standing' | 'walking';
      standTimer: number;
      conf: string;
      color: string;
      stepPhase: number;
    }

    const camState = {
      d: initialDens,
      t: 0,
      crossed: Math.round(initialPeople * 7),
      people: [] as PersonParticle[]
    };

    const isQueue = Boolean(activeCameraModal && activeCameraModal.kind === 'queue');
    const isArea = Boolean(!isExit && !isQueue);

    const n = Math.max(10, Math.min(48, Math.round(initialDens * 13)));
    for (let i = 0; i < n; i++) {
      const lane = i % 3;
      const laneOffset = (Math.random() - 0.5) * 0.05;
      const startStanding = isArea && Math.random() < 0.55;

      camState.people.push({
        lane,
        laneOffset,
        x: isArea ? 0.12 + Math.random() * 0.76 : laneCenters[lane] + laneOffset,
        y: isExit ? 0.36 + Math.random() * 0.64 : 0.35 + Math.random() * 0.60,
        vx: (Math.random() - 0.5) * 0.0006,
        vy: (Math.random() - 0.3) * 0.0008,
        s: 0.0016 + Math.random() * 0.0014,
        state: startStanding ? 'standing' : 'walking',
        standTimer: 100 + Math.floor(Math.random() * 300),
        conf: (0.88 + Math.random() * 0.10).toFixed(2),
        color: devoteeColors[i % devoteeColors.length],
        stepPhase: Math.random() * Math.PI * 2
      });
    }

    let animId: number;

    const render = () => {
      const g = cv.getContext("2d");
      if (!g) return;
      const W = (cv.width = cv.clientWidth || 640);
      const H = (cv.height = cv.clientHeight || 360);
      camState.t += 1;

      // Dynamically track latest density without restarting animation
      const q = queueStateRef.current;
      const hcs = holdingComplexesRef.current;
      let curHoldingRoom: { currentCount: number; capacity: number } | null = null;
      for (const ha of hcs) {
        for (const rm of ha.rooms) {
          if (rm.cams.includes(activeCameraModal.id)) {
            curHoldingRoom = rm;
            break;
          }
        }
        if (curHoldingRoom) break;
      }

      const curDens = curHoldingRoom
        ? Math.max(0.6, Math.min(5.5, (curHoldingRoom.currentCount / curHoldingRoom.capacity) * 3.8))
        : line && stage ? cellDens(line.id, stage, q.cells)
        : stage ? xDens(stage as any, q.xcells) : 1.4;
      camState.d = curDens;

      // Adjust particle count smoothly if density shifts
      const targetN = Math.max(10, Math.min(48, Math.round(curDens * 13)));
      if (camState.people.length < targetN && Math.random() < 0.25) {
        const lane = Math.floor(Math.random() * 3);
        const laneOffset = (Math.random() - 0.5) * 0.05;
        const startStanding = isArea && Math.random() < 0.5;

        camState.people.push({
          lane,
          laneOffset,
          x: isArea ? 0.12 + Math.random() * 0.76 : laneCenters[lane] + laneOffset,
          y: isExit ? 1.02 : 0.34,
          vx: (Math.random() - 0.5) * 0.0006,
          vy: (Math.random() - 0.3) * 0.0008,
          s: 0.0016 + Math.random() * 0.0014,
          state: startStanding ? 'standing' : 'walking',
          standTimer: 120 + Math.floor(Math.random() * 260),
          conf: (0.88 + Math.random() * 0.10).toFixed(2),
          color: devoteeColors[Math.floor(Math.random() * devoteeColors.length)],
          stepPhase: Math.random() * Math.PI * 2
        });
      }

      // 1. Dark floor background
      g.fillStyle = isExit ? "#0A1017" : isArea ? "#101824" : "#0F1722";
      g.fillRect(0, 0, W, H);

      // 2. Horizon & Wall Structure
      const horizonY = H * 0.30;
      g.fillStyle = isExit ? "#070C12" : isArea ? "#09101A" : "#0B1118";
      g.fillRect(0, 0, W, horizonY);

      if (isExit) {
        // ================= EXIT ARCHWAY & GANTRY PORTAL =================
        const gateW = W * 0.64;
        const gateX = (W - gateW) / 2;
        g.fillStyle = "rgba(16, 185, 129, 0.12)";
        g.fillRect(gateX, horizonY - 32, gateW, 32);

        g.strokeStyle = "#10B981";
        g.lineWidth = 1.5;
        g.strokeRect(gateX, horizonY - 32, gateW, 32);

        // Illuminated Green LED Exit Signboard
        g.fillStyle = "#064E3B";
        g.fillRect(gateX + 4, horizonY - 28, gateW - 8, 24);
        g.fillStyle = "#10B981";
        g.font = 'bold 10px "Outfit", system-ui, sans-serif';
        g.textAlign = "center";
        g.fillText("◀ EXIT · ప్రధాన నిష్క్రమణ ద్వారం · DISPERSAL OUTFLOW ▶", W * 0.5, horizonY - 12);
        g.textAlign = "left";

        // Exit Archway Pillars
        g.fillStyle = "#1E293B";
        g.fillRect(gateX - 12, horizonY - 38, 12, 40);
        g.fillRect(gateX + gateW, horizonY - 38, 12, 40);
        g.strokeStyle = "#10B981";
        g.lineWidth = 1;
        g.strokeRect(gateX - 12, horizonY - 38, 12, 40);
        g.strokeRect(gateX + gateW, horizonY - 38, 12, 40);
      }

      // Perspective floor lines
      g.strokeStyle = isExit ? "#162736" : isArea ? "#162230" : "#1B2A38";
      g.lineWidth = 1;
      for (let i = 0; i < 9; i++) {
        const y = horizonY + (H * 0.70) * Math.pow(i / 8, 1.8);
        g.beginPath();
        g.moveTo(W * 0.5 - (y - horizonY) * 1.15, y);
        g.lineTo(W * 0.5 + (y - horizonY) * 1.15, y);
        g.stroke();
      }

      // ================= BARRICADES (ONLY IN QUEUE OR EXIT CORRIDOR) =================
      if (isQueue) {
        // Steel Barricade Rails for Queue Lines
        const barricadeNormX = [-0.48, -0.16, 0.16, 0.48];
        barricadeNormX.forEach(bx => {
          // Steel Top Rail
          g.strokeStyle = "#475569";
          g.lineWidth = 2.5;
          g.beginPath();
          g.moveTo(W * 0.5 + bx * W * 0.42, horizonY);
          g.lineTo(W * 0.5 + bx * W * 1.08, H);
          g.stroke();

          // Steel Bottom Rail
          g.strokeStyle = "#334155";
          g.lineWidth = 1.8;
          g.beginPath();
          g.moveTo(W * 0.5 + bx * W * 0.42, horizonY + 8);
          g.lineTo(W * 0.5 + bx * W * 1.08, H - 8);
          g.stroke();

          // Vertical Stanchion Posts along barricade
          for (let post = 1; post <= 5; post++) {
            const depth = Math.pow(post / 5, 1.4);
            const py = horizonY + (H * 0.70) * depth;
            const px = W * 0.5 + bx * W * (0.42 + depth * 0.66);
            const postH = 8 + depth * 22;

            g.strokeStyle = "#94A3B8";
            g.lineWidth = 1.8;
            g.beginPath();
            g.moveTo(px, py);
            g.lineTo(px, py - postH);
            g.stroke();

            // Chrome Cap Ball on post
            g.fillStyle = "#E2E8F0";
            g.beginPath();
            g.arc(px, py - postH, 1.5 + depth * 1.5, 0, Math.PI * 2);
            g.fill();
          }
        });
      } else if (isExit) {
        // Side Corridor Boundary Rails
        [-0.48, 0.48].forEach(bx => {
          g.strokeStyle = "#334155";
          g.lineWidth = 2;
          g.beginPath();
          g.moveTo(W * 0.5 + bx * W * 0.42, horizonY);
          g.lineTo(W * 0.5 + bx * W * 1.08, H);
          g.stroke();
        });
      }

      if (isExit) {
        // Directional Exit Floor Chevrons (Pointing Away into the Exit)
        g.fillStyle = "rgba(16, 185, 129, 0.25)";
        g.font = 'bold 9px "Inter", monospace';
        g.textAlign = "center";
        for (let step = 1; step <= 4; step++) {
          const arrowY = horizonY + (H * 0.70) * Math.pow(step / 5, 1.5);
          g.fillText("▲   ▲   ▲   DISPERSAL FLOW   ▲   ▲   ▲", W * 0.5, arrowY);
        }
        g.textAlign = "left";

        // Exit Dispersal Counting Boundary Line
        const cy = horizonY + H * 0.18;
        g.strokeStyle = "#10B981";
        g.lineWidth = 1.6;
        g.setLineDash([6, 4]);
        g.beginPath();
        g.moveTo(W * 0.12, cy);
        g.lineTo(W * 0.88, cy);
        g.stroke();
        g.setLineDash([]);

        g.fillStyle = "#10B981";
        g.font = '600 10px "Inter", system-ui, sans-serif';
        g.fillText("COUNT LINE", W * 0.12, cy - 6);
      } else if (isQueue) {
        // Yellow Entry Counting Boundary line across midpoint
        const cy = H * 0.76;
        g.strokeStyle = "#E9C46A";
        g.lineWidth = 1.5;
        g.setLineDash([5, 4]);
        g.beginPath();
        g.moveTo(W * 0.10, cy);
        g.lineTo(W * 0.90, cy);
        g.stroke();
        g.setLineDash([]);

        g.fillStyle = "#E9C46A";
        g.font = '600 10px "Inter", system-ui, sans-serif';
        g.fillText("COUNT LINE", W * 0.10, cy - 7);
      }

      // ================= DEVOTEE ANIMATION & AI BOUNDING BOXES =================
      // Sort devotees by depth (y) so foreground people render over background people
      const sortedIndices = camState.people
        .map((p, idx) => ({ p, idx }))
        .sort((a, b) => a.p.y - b.p.y);

      for (const item of sortedIndices) {
        const p = item.p;
        const idx = item.idx;

        if (isExit) {
          // REVERSE MOVEMENT: Devotees walk AWAY towards the exit portal (decreasing y)
          p.y -= p.s * (1.6 - Math.min(1.2, camState.d * 0.28));
          p.stepPhase += p.s * 85;

          if (p.y < 0.33) {
            if (camState.people.length > targetN) {
              camState.people.splice(idx, 1);
              continue;
            }
            p.y = 1.02;
            p.lane = Math.floor(Math.random() * 3);
            p.laneOffset = (Math.random() - 0.5) * 0.05;
            p.x = laneCenters[p.lane] + p.laneOffset;
            camState.crossed++;
            p.conf = (0.88 + Math.random() * 0.10).toFixed(2);
            p.color = devoteeColors[Math.floor(Math.random() * devoteeColors.length)];
          }
        } else if (isQueue) {
          // FORWARD QUEUE MOVEMENT: Devotees walk FORWARD in queue lanes towards camera
          p.y += p.s * (1.6 - Math.min(1.2, camState.d * 0.28));
          p.stepPhase += p.s * 85;

          if (p.y > 1.05) {
            if (camState.people.length > targetN) {
              camState.people.splice(idx, 1);
              continue;
            }
            p.y = 0.34;
            p.lane = Math.floor(Math.random() * 3);
            p.laneOffset = (Math.random() - 0.5) * 0.05;
            p.x = laneCenters[p.lane] + p.laneOffset;
            camState.crossed++;
            p.conf = (0.88 + Math.random() * 0.10).toFixed(2);
            p.color = devoteeColors[Math.floor(Math.random() * devoteeColors.length)];
          }
        } else {
          // OPEN AREA MOVEMENT: Devotees stand naturally or stroll around the hall
          if (p.state === 'standing') {
            p.stepPhase += 0.025; // Gentle breathing / posture sway
            p.standTimer--;
            if (p.standTimer <= 0) {
              // Transition to gentle walking
              p.state = 'walking';
              p.vx = (Math.random() - 0.5) * 0.0006;
              p.vy = (Math.random() - 0.4) * 0.0007;
              p.standTimer = 180 + Math.floor(Math.random() * 300);
            }
          } else {
            // Walking / strolling
            p.x += p.vx;
            p.y += p.vy;
            p.stepPhase += Math.hypot(p.vx, p.vy) * 110;

            // Soft floor boundary constraints
            if (p.x < 0.12) { p.x = 0.12; p.vx = Math.abs(p.vx); }
            if (p.x > 0.88) { p.x = 0.88; p.vx = -Math.abs(p.vx); }
            if (p.y < 0.34) { p.y = 0.34; p.vy = Math.abs(p.vy); }
            if (p.y > 0.94) { p.y = 0.94; p.vy = -Math.abs(p.vy); }

            p.standTimer--;
            if (p.standTimer <= 0) {
              // Transition to standing
              p.state = 'standing';
              p.standTimer = 120 + Math.floor(Math.random() * 300);
            }
          }
        }

        const depth = Math.max(0, Math.min(1, (p.y - 0.30) / 0.70));
        const laneX = isArea ? p.x : laneCenters[p.lane] + p.laneOffset;
        const isWalking = p.state === 'walking' || isQueue || isExit;
        const sway = isWalking ? Math.sin(p.stepPhase) * (1.2 + depth * 1.8) : Math.sin(p.stepPhase) * (0.4 + depth * 0.6);
        const px = W * 0.5 + (laneX - 0.5) * W * (0.34 + depth * 0.74) + sway;
        const py = horizonY + (H * 0.70) * Math.pow(depth, 1.05);
        const h = Math.max(16, 20 + depth * 78);
        const w = h * 0.44;

        // Devotee Silhouette
        if (isExit) {
          // Back-view Devotee (walking away into exit)
          g.fillStyle = p.color;
          g.beginPath();
          g.ellipse(px, py - h * 0.45, w * 0.44, h * 0.38, 0, 0, Math.PI * 2);
          g.fill();

          g.fillStyle = "#1E293B";
          g.beginPath();
          g.arc(px, py - h * 0.82, w * 0.30, 0, Math.PI * 2);
          g.fill();

          const boxColor = "#10B981";
          g.strokeStyle = boxColor;
          g.lineWidth = 1.2;
          g.strokeRect(px - w / 2 - 3, py - h - 3, w + 6, h + 6);

          if (depth > 0.20) {
            g.fillStyle = boxColor;
            g.font = 'bold 9px "Inter", system-ui, sans-serif';
            g.fillText(`▲ exit ${p.conf}`, px - w / 2 - 3, py - h - 7);
          }
        } else {
          // Front-view Devotee (standing or walking forward)
          const legPhase = isWalking ? Math.sin(p.stepPhase) : 0;

          // Legs
          if (depth > 0.25) {
            g.strokeStyle = "#475569";
            g.lineWidth = Math.max(1.5, 2.5 * depth);
            // Left Leg
            g.beginPath();
            g.moveTo(px - w * 0.22, py - h * 0.25);
            g.lineTo(px - w * 0.22 + legPhase * 4 * depth, py);
            g.stroke();
            // Right Leg
            g.beginPath();
            g.moveTo(px + w * 0.22, py - h * 0.25);
            g.lineTo(px + w * 0.22 - legPhase * 4 * depth, py);
            g.stroke();
          }

          // Traditional Devotee Attire
          g.fillStyle = p.color;
          g.beginPath();
          g.ellipse(px, py - h * 0.50, w * 0.46, h * 0.36, 0, 0, Math.PI * 2);
          g.fill();

          // Head & Tilak
          g.fillStyle = "#E0A96D";
          g.beginPath();
          g.arc(px, py - h * 0.82, w * 0.28, 0, Math.PI * 2);
          g.fill();

          if (depth > 0.35) {
            g.fillStyle = "#DC2626";
            g.fillRect(px - 1, py - h * 0.86, 2, 4);
          }

          // AI Detection Bounding Box (Neon Green / Cyan)
          const boxColor = isArea && p.state === 'standing' ? '#38BDF8' : '#25D0A3';
          g.strokeStyle = boxColor;
          g.lineWidth = 1.2;
          g.strokeRect(px - w / 2 - 3, py - h - 3, w + 6, h + 6);

          // Bounding Box Corner Reticle Accents
          const cl = 4;
          g.strokeStyle = boxColor;
          g.lineWidth = 1.8;
          g.beginPath();
          g.moveTo(px - w / 2 - 3, py - h - 3 + cl);
          g.lineTo(px - w / 2 - 3, py - h - 3);
          g.lineTo(px - w / 2 - 3 + cl, py - h - 3);

          g.moveTo(px + w / 2 + 3 - cl, py - h - 3);
          g.lineTo(px + w / 2 + 3, py - h - 3);
          g.lineTo(px + w / 2 + 3, py - h - 3 + cl);
          g.stroke();

          // Person Detection Confidence Label
          if (depth > 0.22) {
            g.fillStyle = boxColor;
            g.font = 'bold 8.5px "Inter", system-ui, sans-serif';
            const tag = isArea && p.state === 'standing' ? `person ${p.conf} [standing]` : `person ${p.conf}`;
            g.fillText(tag, px - w / 2 - 3, py - h - 7);
          }
        }
      }

      // Top CCTV HUD Bar
      g.fillStyle = "rgba(8, 15, 22, 0.90)";
      g.fillRect(0, 0, W, 26);
      g.fillStyle = "#E8EFF7";
      g.font = '600 11px "Inter", system-ui, sans-serif';
      const now = new Date();
      g.fillText(
        `${activeCameraModal.id}   ${now.toLocaleTimeString("en-IN", { hour12: false })}   ${isExit ? "EXIT DISPERSAL CONCOURSE" : isArea ? "AREA SURVEILLANCE FEED" : "QUEUE CONCOURSE"}`,
        10,
        17
      );

      // Red / Amber Blinking Live Recording Dot
      const blink = Math.floor(camState.t / 25) % 2 === 0;
      g.fillStyle = blink ? (isExit ? "#10B981" : "#FF4F42") : "#064E3B";
      g.beginPath();
      g.arc(W - 110, 13, 4, 0, Math.PI * 2);
      g.fill();
      g.fillStyle = "#E8EFF7";
      g.font = '600 10.5px "Inter", system-ui, sans-serif';
      g.fillText(isExit ? "OUTFLOW LIVE" : "LIVE CCTV", W - 98, 17);

      // Bottom CCTV HUD Bar
      g.fillStyle = "rgba(8, 15, 22, 0.90)";
      g.fillRect(0, H - 26, W, 26);
      g.fillStyle = isExit ? "#10B981" : "#25D0A3";
      g.font = '600 11px "Inter", system-ui, sans-serif';
      if (isExit) {
        g.fillText(
          `OUT ${fmt(camState.crossed)} (DISPERSED)   density ${camState.d.toFixed(1)} p/m²   exiting tracks ${camState.people.length}`,
          10,
          H - 9
        );
      } else if (isArea) {
        g.fillText(
          `PRESENT ${fmt(camState.crossed)}   density ${camState.d.toFixed(1)} p/m²   standing & moving tracks ${camState.people.length}`,
          10,
          H - 9
        );
      } else {
        g.fillText(
          `IN ${fmt(camState.crossed)}   density ${camState.d.toFixed(1)} p/m²   queue tracks ${camState.people.length}`,
          10,
          H - 9
        );
      }

      animId = requestAnimationFrame(render);
    };

    render();

    return () => {
      cancelAnimationFrame(animId);
    };
  }, [activeCameraModal?.id, activeCameraModal?.stream, isExit]);

  if (!activeCameraModal) return null;

  // Check if camera belongs to a holding room
  let holdingRoom: { currentCount: number; capacity: number } | null = null;
  for (const ha of holdingComplexes) {
    for (const rm of ha.rooms) {
      if (rm.cams.includes(activeCameraModal.id)) {
        holdingRoom = rm;
        break;
      }
    }
    if (holdingRoom) break;
  }

  const stage = QSTAGES.find(s => s.id === activeCameraModal.stage) || XSTAGES.find(s => s.id === activeCameraModal.stage);
  const line = QLINES.find(l => l.id === activeCameraModal.line);
  const dens = holdingRoom
    ? Math.max(0.6, Math.min(5.5, (holdingRoom.currentCount / holdingRoom.capacity) * 3.8))
    : line && stage ? cellDens(line.id, stage, queueState.cells)
    : stage ? xDens(stage as any, queueState.xcells) : 1.4;
  const people = holdingRoom
    ? holdingRoom.currentCount
    : line && stage ? (queueState.cells[qKey(line.id, stage.id)] || 0)
    : stage ? (queueState.xcells[stage.id] || 0) : queueState.hold;

  return (
    <div
      className="fixed inset-0 bg-black/60 dark:bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-in fade-in duration-200"
      id="camModal"
      onClick={e => {
        if ((e.target as HTMLElement).id === "camModal") closeCamera();
      }}
    >
      <div className="w-[740px] max-w-full bg-white dark:bg-[#181b22] border border-neutral-200/80 dark:border-[#262b37] rounded-xl shadow-2xl overflow-hidden flex flex-col transition-colors duration-200">
        {/* Header Bar */}
        <div className="flex items-center gap-3 px-4 py-2.5 bg-neutral-50/90 dark:bg-[#14171f] border-b border-neutral-200/80 dark:border-[#262b37]">
          <span className={`font-mono font-bold text-[11px] px-2 py-0.5 rounded border flex items-center gap-1.5 ${
            isExit
              ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-600 dark:text-emerald-400'
              : 'bg-blue-500/15 border-blue-500/30 text-blue-600 dark:text-blue-400'
          }`}>
            {isExit ? <LogOut className="w-3 h-3 text-emerald-500" /> : <Video className="w-3 h-3 text-blue-500" />}
            {activeCameraModal.id}
          </span>
          <span className="text-xs font-semibold text-neutral-900 dark:text-neutral-100 flex-1 truncate">
            {activeCameraModal.sector} · {activeCameraModal.place}
            {line ? ` · ${line.name}` : ""}
            {isExit ? " · [Exit / Dispersal Concourse]" : ""}
          </span>
          <button
            className="bg-neutral-100 dark:bg-[#1f2430] hover:bg-neutral-200 dark:hover:bg-[#262b37] border border-neutral-200/80 dark:border-[#2a3040] text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 rounded-md p-1 px-2.5 text-[10px] font-mono uppercase transition cursor-pointer flex items-center gap-1 active:scale-95"
            onClick={closeCamera}
          >
            <span>Close</span>
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Video / Synthetic Canvas Area */}
        <div className="relative aspect-video bg-[#03080E] overflow-hidden flex items-center justify-center border-b border-neutral-200/80 dark:border-[#262b37]">
          <canvas
            ref={canvasRef}
            id="camCanvas"
            className="w-full h-full block"
            style={{ display: activeCameraModal.stream ? "none" : "block" }}
          />
          <video
            ref={videoRef}
            id="camVideo"
            muted
            playsInline
            controls
            className="w-full h-full object-cover"
            style={{ display: activeCameraModal.stream ? "block" : "none" }}
          />
        </div>

        {/* Telemetry Footer */}
        <div className="grid grid-cols-3 gap-2.5 p-3 bg-neutral-50/90 dark:bg-[#14171f] text-[11px]">
          <div className="p-2.5 rounded-lg bg-white dark:bg-[#181b22] border border-neutral-200/60 dark:border-[#222733] shadow-xs">
            <span className="text-[9px] text-neutral-500 dark:text-neutral-400 uppercase font-semibold tracking-wider block mb-0.5 flex items-center gap-1">
              {isExit ? <ArrowUpRight className="w-2.5 h-2.5 text-emerald-500" /> : <Users className="w-2.5 h-2.5 text-blue-500" />}
              {isExit ? "Exiting Devotees" : "Standing Here"}
            </span>
            <span className={`font-mono text-base font-bold block ${isExit ? 'text-emerald-600 dark:text-emerald-400' : 'text-neutral-900 dark:text-neutral-100'}`}>
              {fmt(people)}
            </span>
          </div>
          <div className="p-2.5 rounded-lg bg-white dark:bg-[#181b22] border border-neutral-200/60 dark:border-[#222733] shadow-xs">
            <span className="text-[9px] text-neutral-500 dark:text-neutral-400 uppercase font-semibold tracking-wider block mb-0.5 flex items-center gap-1">
              <Gauge className="w-2.5 h-2.5 text-amber-500" />
              {isExit ? "Exit Density" : "Density"}
            </span>
            <span className="font-mono text-base font-bold block" style={{ color: losColor(dens) }}>
              {dens.toFixed(1)} p/m²
            </span>
          </div>
          <div className="p-2.5 rounded-lg bg-white dark:bg-[#181b22] border border-neutral-200/60 dark:border-[#222733] shadow-xs">
            <span className="text-[9px] text-neutral-500 dark:text-neutral-400 uppercase font-semibold tracking-wider block mb-0.5 flex items-center gap-1">
              <Activity className="w-2.5 h-2.5 text-cyan-500" />
              {isExit ? "Dispersal Flow" : "Condition"}
            </span>
            <span className="font-mono text-base font-bold text-neutral-900 dark:text-neutral-100 block">{isExit ? "Continuous Outflow" : crowdWord(dens)}</span>
          </div>
          <div className="col-span-3 text-[9.5px] text-neutral-500 dark:text-neutral-400 leading-relaxed p-2 rounded-md bg-white/70 dark:bg-[#1f2430]/60 border border-neutral-200/60 dark:border-[#2a3040] font-mono">
            {isExit
              ? "Synthetic operator CCTV feed with reverse-direction AI devotee trajectory tracking towards temple exits."
              : "Synthetic operator feed with real-time AI crowd detection bounding boxes."}
          </div>
        </div>
      </div>
    </div>
  );
};